import { useMutation, useQueryClient } from '@tanstack/react-query';
import { add } from 'date-fns';

import { showLimitOrderToast } from '@/components/TransactionModule/components/TransactionToast/LimitOrderToast/LimitOrderToast';
import { submitAdvancedOrder } from '@/components/TransactionModule/executor/submitAdvancedOrder';

import {
  isAdvancedOrderTypeSupported,
  isLimitOrderSupportedChainId,
  type TriggerOrderType,
} from '@/constants/advancedOrder';
import { CHAIN_IDS } from '@/constants/chain';

import { useMatchaWallets } from '@/hooks/useMatchaWallets';
import { useSubmitLimitOrder } from '@/hooks/useSubmitLimitOrder';

import { referrersStoreSelectors, useReferrerStore } from '@/store/referrals';
import { recentTransactionSelector, useTransactionStore } from '@/store/transaction';

import { refreshOpenLimitOrdersUntilVisible } from '@/utils/0x/orderbook';
import type { TokenResult } from '@/utils/0x/token-registry.types';
import { isChainAddress } from '@/utils/models';

import { getTriggerOrderAmounts, type TriggerOrderUiLocation } from './utils';

type SubmitPostSwapTriggerOrderParams = {
  buyToken: TokenResult;
  buyTokenUsd: number;
  orderType: TriggerOrderType;
  sellAmount: string;
  sellToken: TokenResult;
  sellTokenUsd: number;
  triggerRate: string;
  uiLocation: TriggerOrderUiLocation;
};

export type PlacedTriggerOrder = {
  buyAmount: string;
  orderId: string;
  orderType: TriggerOrderType;
  triggerRate: string;
};

export function usePostSwapTriggerOrder() {
  const queryClient = useQueryClient();
  const { ethereumWallet, solanaWallet } = useMatchaWallets();
  const { referrerId, isExpired } = useReferrerStore(referrersStoreSelectors);
  const { setRecentTransaction } = useTransactionStore(recentTransactionSelector);
  const { submitLimitOrder, phase } = useSubmitLimitOrder();

  const mutation = useMutation<
    PlacedTriggerOrder | undefined,
    Error,
    SubmitPostSwapTriggerOrderParams
  >({
    retry: false,
    mutationFn: async ({
      buyToken,
      buyTokenUsd,
      orderType,
      sellAmount,
      sellToken,
      sellTokenUsd,
      triggerRate: requestedTriggerRate,
      uiLocation,
    }) => {
      const chainId = sellToken.chainId;
      if (
        !isLimitOrderSupportedChainId(chainId) ||
        !isAdvancedOrderTypeSupported(chainId, orderType)
      ) {
        return undefined;
      }

      const rawMaker =
        chainId === CHAIN_IDS.SOLANA ? solanaWallet?.address : ethereumWallet?.address;
      if (!rawMaker || !isChainAddress(rawMaker)) return undefined;

      const { buyAmount, triggerRate } = getTriggerOrderAmounts({
        buyTokenDecimals: buyToken.decimals,
        sellAmount,
        triggerRate: requestedTriggerRate,
      });
      const result = await submitAdvancedOrder({
        chainId,
        sellToken,
        buyToken,
        sellInput: {
          decimal: sellAmount,
          formatted: sellAmount,
          usd: Number(sellAmount) * sellTokenUsd,
        },
        buyInput: {
          decimal: buyAmount,
          formatted: buyAmount,
          usd: Number(buyAmount) * buyTokenUsd,
        },
        orderType,
        matchaReferrer: !isExpired() ? referrerId : undefined,
        uiLocation,
        submit: async () => {
          const submission = await submitLimitOrder({
            chainId,
            maker: rawMaker,
            buyToken,
            sellToken,
            buyAmount,
            sellAmount,
            expiry: add(new Date(), { days: 30 }),
            orderType,
            triggerCrossPrice: triggerRate,
          });
          if (submission.provider !== 'definitive') {
            throw new Error(`Unexpected trigger order provider: ${submission.provider}`);
          }
          return submission;
        },
        refreshOpenLimitOrders: (identity) => {
          void refreshOpenLimitOrdersUntilVisible(queryClient, rawMaker, identity);
        },
        setRecentTransaction: () => setRecentTransaction(true),
      });

      showLimitOrderToast({
        status: result.success ? 'submitted' : 'submissionFailed',
        sellAmount,
        buyAmount,
        sellToken,
        buyToken,
        orderType,
      });
      if (!result.success) return undefined;

      return {
        buyAmount,
        orderId: result.submission.orderIdentifier,
        orderType,
        triggerRate,
      };
    },
  });

  return {
    submitPostSwapTriggerOrder: mutation.mutateAsync,
    phase,
    isPending: mutation.isPending,
  };
}
